import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  InfoOutlined as InfoIcon,
  SecurityOutlined as ShieldIcon,
  PeopleOutlined as PeopleIcon,
  HistoryOutlined as HistoryIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import RoleBasicInfoSection from './RoleManagement/components/RoleBasicInfoSection';
import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';
import RoleUsersSection from './RoleManagement/components/RoleUsersSection';
import RoleHistorySection from './RoleManagement/components/RoleHistorySection';
import { useGetRole } from '@/api/hooks/roles/useGetRole';
import { useUpdateRole } from '@/api/hooks/roles/useUpdateRole';
import CpdActionButton from '@/components/ui/CpdActionButton';

const RoleDetails = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const basicInfoRef = useRef(null);

  const { role, loading, error: fetchError, refetch } = useGetRole(roleId);
  const {
    updateRole,
    loading: updateLoading,
    error: updateError,
  } = useUpdateRole();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  });
  const [basicInfo, setBasicInfo] = useState({});
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});

  useEffect(() => {
    if (role) {
      setFacilityPermissions(role.facility_permissions || {});
      setOrganizationPermissions(role.organization_permissions || {});
    }
  }, [role]);

  const handleBasicInfoChange = (newBasicInfo) => {
    setBasicInfo(newBasicInfo);
  };

  const handleFacilityPermissionChange = (newPermissions) => {
    setFacilityPermissions(newPermissions);
  };

  const handleOrganizationPermissionChange = (newPermissions) => {
    setOrganizationPermissions(newPermissions);
  };

  const handleApplyTemplate = (template) => {
    if (template.facilityPermissions) {
      setFacilityPermissions(template.facilityPermissions);
    }
    if (template.organizationPermissions) {
      setOrganizationPermissions(template.organizationPermissions);
    }
    if (template.permissions && !template.facilityPermissions) {
      setFacilityPermissions(template.permissions);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newValue.toString());
    }
    setSearchParams(newSearchParams);
  };

  const handleSave = async () => {
    try {
      let formData;
      let successMsg;

      if (activeTab === 0) {
        if (basicInfoRef.current && basicInfoRef.current.validateForm()) {
          formData = {
            name: basicInfo.name,
            description: basicInfo.description,
            organization_id: basicInfo.organizationId,
            is_default: basicInfo.isDefault,
            has_all_facilities_access: basicInfo.hasAllFacilitiesAccess,
            facility_permissions: role.facility_permissions,
            organization_permissions: role.organization_permissions,
          };
          successMsg = 'Basic information updated successfully';
        } else {
          return;
        }
      } else if (activeTab === 1) {
        formData = {
          name: role.name,
          description: role.description,
          organization_id: role.organization_id,
          facility_permissions: role.facility_permissions,
          organization_permissions: organizationPermissions,
        };
        successMsg = 'Organization permissions updated successfully';
      } else if (activeTab === 2) {
        formData = {
          name: role.name,
          description: role.description,
          organization_id: role.organization_id,
          facility_permissions: facilityPermissions,
          organization_permissions: role.organization_permissions,
        };
        successMsg = 'Facility permissions updated successfully';
      }

      if (formData) {
        await updateRole(roleId, formData);

        await refetch();

        setSuccessMessage(successMsg);
        setShowSuccessAlert(true);

        setTimeout(() => {
          const successAlert = document.getElementById('success-alert');
          if (successAlert) {
            successAlert.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  const handleBack = () => {
    navigate('/admin/roles');
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  };

  if (loading) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (fetchError) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='error' sx={{ m: 2 }}>
          {fetchError}
        </Alert>
      </PageLayout>
    );
  }

  if (!role) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='warning' sx={{ m: 2 }}>
          Role not found
        </Alert>
      </PageLayout>
    );
  }

  const isFormDisabled =
    updateLoading || (activeTab === 0 && !basicInfoRef.current?.isFormValid);

  return (
    <PageLayout
      height='auto'
      padding={{ pt: 2, px: 2, pb: 4 }}
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
            <SecurityIcon sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              Role Details: {role.name}
            </Typography>
          </Box>
        </CardHeader>
      }>
      {showSuccessAlert && successMessage && (
        <Alert
          id='success-alert'
          severity='success'
          sx={{ mb: 3 }}
          onClose={handleSuccessClose}>
          {successMessage}
        </Alert>
      )}

      {updateError && (
        <Alert id='error-alert' severity='error' sx={{ mb: 3 }}>
          {updateError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'>
          <Tab
            icon={<InfoIcon />}
            iconPosition='start'
            label='Basic Information'
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition='start'
            label='Organization Permissions'
          />
          <Tab
            icon={<ShieldIcon />}
            iconPosition='start'
            label='Facility Permissions'
          />
          <Tab
            icon={
              <Badge
                badgeContent={role?.assigned_users_count || 0}
                color='info'
                showZero
                sx={{
                  '& .MuiBadge-badge': {
                    color: 'white',
                    fontWeight: 'bold',
                  },
                }}>
                <PeopleIcon />
              </Badge>
            }
            iconPosition='start'
            label='Assigned Users'
          />
          <Tab icon={<HistoryIcon />} iconPosition='start' label='History' />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <RoleBasicInfoSection
          ref={basicInfoRef}
          role={role}
          onFormChange={handleBasicInfoChange}
        />
      )}

      {activeTab === 1 && (
        <PermissionsSection
          permissions={organizationPermissions}
          onPermissionChange={handleOrganizationPermissionChange}
          onApplyTemplate={handleApplyTemplate}
          permissionLevel='organization'
        />
      )}

      {activeTab === 2 && (
        <PermissionsSection
          permissions={facilityPermissions}
          onPermissionChange={handleFacilityPermissionChange}
          onApplyTemplate={handleApplyTemplate}
          permissionLevel='facility'
        />
      )}

      {activeTab === 3 && <RoleUsersSection roleId={roleId} />}

      {activeTab === 4 && <RoleHistorySection roleId={roleId} />}

      {activeTab < 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
          <CpdActionButton
            onClick={handleSave}
            loading={updateLoading}
            startIcon={<SaveIcon sx={{ color: 'white' }} />}
            disabled={isFormDisabled}>
            {updateLoading ? 'Saving...' : 'Save Changes'}
          </CpdActionButton>
        </Box>
      )}
    </PageLayout>
  );
};

export default RoleDetails;
